/**
 * TypeScript Color Types - IDE Autocomplete Support
 *
 * Provides type-safe color IDs with full IDE autocomplete.
 * Prevents typos and invalid color references at compile time.
 *
 * @example
 * ```typescript
 * import { getTypedColor, ColorId } from './color-types';
 *
 * // ✅ IDE suggests valid color IDs
 * const color = getTypedColor('advantage');
 *
 * // ❌ TypeScript error: 'advatage' is not a valid ColorId
 * const typo = getTypedColor('advatage');
 * ```
 */

import {colorRegistry, type ColorInfo} from './registry'

// ============================================================================
// TYPE-SAFE COLOR IDS
// ============================================================================

/**
 * Union type of all valid color IDs from the registry
 * This enables IDE autocomplete and compile-time validation
 */
type ColorId = keyof typeof colorRegistry

// ============================================================================
// TYPE-SAFE COLOR GETTERS
// ============================================================================

/**
 * Get color by ID with type safety
 * @param id - Must be a valid ColorId (IDE will autocomplete)
 * @returns ColorInfo for the color
 */
export function getTypedColor(id: ColorId): ColorInfo {
	const color = colorRegistry[id]
	if (!color) {
		throw new Error(`Color not found: ${id}`)
	}
	return color
}
